import { usePathname, useRouter } from "expo-router";
import {
  Text,
  Pressable,
  ScrollView,
  LayoutChangeEvent,
  Animated,
} from "react-native";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";
import { useSettingsData } from "../../../contexts/SettingsDataContext";
import { useActiveAgent } from "../../../contexts/ActiveAgentContext";
import {
  Ban,
  LucideIcon,
  MessageCircle,
  Mic,
  Settings,
  ShoppingBag,
} from "lucide-react-native";
import { useViewport } from "../../../hooks/useViewport";

type Tab = {
  name: string;
  label: string;
  path: string;
  icon: LucideIcon;
};

const tabs: Tab[] = [
  {
    name: "index",
    label: "General",
    path: "/settings",
    icon: Settings,
  },
  {
    name: "voice",
    label: "Voice",
    path: "/settings/voice",
    icon: Mic,
  },
  {
    name: "ordering",
    label: "Ordering",
    path: "/settings/ordering",
    icon: ShoppingBag,
  },
  {
    name: "faq",
    label: "FAQ",
    path: "/settings/faq",
    icon: MessageCircle,
  },
  {
    name: "blocked",
    label: "Blocked Callers",
    path: "/settings/blocked",
    icon: Ban,
  },
];

const ORDERING_TAB_NAME = "ordering";

export default function SettingsTabs() {
  const router = useRouter();
  const pathname = usePathname();
  const posthog = usePostHog();
  const scrollViewRef = useRef<ScrollView>(null);
  const [scrollViewWidth, setScrollViewWidth] = useState(0);
  const { isOrderingAgent } = useSettingsData();
  const { activeAgent } = useActiveAgent();

  const TAB_BAR_HEIGHT = 53;

  useEffect(() => {
    const currentTabIndex = tabs.findIndex((tab) => tab.path === pathname);
    if (currentTabIndex !== -1) {
      scrollTabToCenter(currentTabIndex);
    }
  }, [pathname, scrollViewWidth]); // Include scrollViewWidth to ensure we have the width before scrolling

  function scrollTabToCenter(tabIndex: number) {
    if (!scrollViewWidth) return;
    const totalWidth = tabs.length * (scrollViewWidth / tabs.length);
    let scrollPosition =
      (totalWidth - scrollViewWidth) / 2 +
      (scrollViewWidth / tabs.length) * tabIndex;
    if (tabIndex <= 2) {
      scrollPosition = 0;
    }
    scrollViewRef.current?.scrollTo({ x: scrollPosition, animated: true });
  }

  const lastScrollTime = useRef(Date.now());
  const lastScrollY = useRef(0);
  const { isMobile } = useViewport();
  const height = useRef(new Animated.Value(TAB_BAR_HEIGHT)).current;
  const handleScroll = (event: any) => {
    if (isMobile) {
      const currentScrollY = event.nativeEvent?.contentOffset?.y ?? 0;
      const currentTime = Date.now();

      const layoutHeight = event.nativeEvent.layoutMeasurement.height;
      const contentHeight = event.nativeEvent.contentSize.height;
      const isAtBottom = layoutHeight + currentScrollY >= contentHeight - 20; // 20px threshold

      const timeDiff = currentTime - lastScrollTime.current;
      const scrollDiff = currentScrollY - lastScrollY.current;

      const velocity = Math.abs(scrollDiff / timeDiff);
      const VELOCITY_THRESHOLD = 0.8;

      // Scrolling down
      if (scrollDiff > 0 && currentScrollY > 60) {
        if (velocity > VELOCITY_THRESHOLD) {
          Animated.parallel([
            Animated.spring(height, {
              toValue: 0,
              useNativeDriver: false,
              tension: 80,
              friction: 12,
            }),
          ]).start();
        }
      }
      // Scrolling up
      if (scrollDiff < 0 || currentScrollY <= 60) {
        if (!isAtBottom) {
          Animated.parallel([
            Animated.spring(height, {
              toValue: TAB_BAR_HEIGHT,
              useNativeDriver: false,
              tension: 80,
              friction: 12,
            }),
          ]).start();
        }
      }
      lastScrollY.current = currentScrollY;
      lastScrollTime.current = currentTime;
    }
  };

  useEffect(() => {
    if (isMobile) {
      const handleScrollEvent = (e: any) => {
        handleScroll(e.detail);
      };
      document.addEventListener("settingsScroll", handleScrollEvent);
      return () =>
        document.removeEventListener("settingsScroll", handleScrollEvent);
    }
  }, []);

  if (!activeAgent) {
    return null;
  }
  return (
    <Animated.View
      style={{
        height,
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        overflow: "hidden",
      }}
    >
      <ScrollView
        ref={scrollViewRef}
        horizontal
        onLayout={(e: LayoutChangeEvent) =>
          setScrollViewWidth(e.nativeEvent.layout.width)
        }
        className="border-b border-gray-200 bg-white flex-grow-0 pb-1"
      >
        {tabs.map((tab, index) => {
          const isActive = pathname === tab.path;
          if (!isOrderingAgent && tab.name === ORDERING_TAB_NAME) return null;
          return (
            <Pressable
              key={tab.name}
              // border-[#FF1112]
              className={`px-4 py-3 pt-4 flex-row items-center space-x-2 ${
                isActive ? "border-b-2 border-blue-500" : ""
              }`}
              onPress={() => {
                posthog.capture("settings_tab_pressed", {
                  tab: tab.name,
                });
                router.push({
                  pathname: tab.path,
                });
                scrollTabToCenter(index);
              }}
            >
              {/* #FF1112 */}
              <tab.icon size={16} color={isActive ? "#3b82f6" : "#6B7280"} />
              {/* text-[#FF1112] */}
              <Text
                className={`${
                  isActive ? "text-blue-600 font-medium" : "text-gray-600"
                }`}
              >
                {tab.label}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>
    </Animated.View>
  );
}
